import apiClient from "../../utilities/apiClient";
import GlobalConfig from "../../Config";
import { cleanInventoryProductName } from "../purchaseStock.api";

/** Shared PO list page size (POS `start`/`size`). */
export const PURCHASE_ORDERS_PAGE_SIZE = 200;

export const getActiveCustomers = (token) => {
  const url = GlobalConfig.basePath + "/customers?status=Active&start=0&size=200";
  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + (token || "my-token"),
    },
  }).then((response) => response.json());
};

export const extractPurchaseOrderId = (poOrResult) => {
  if (poOrResult == null) return null;
  if (typeof poOrResult === "string" || typeof poOrResult === "number") {
    const s = String(poOrResult).trim();
    return s && s !== "null" && s !== "undefined" ? s : null;
  }
  const entity = Array.isArray(poOrResult) ? poOrResult[0] : poOrResult;
  if (!entity || typeof entity !== "object") return null;
  // Prefer id over null poid from withoutDetails API
  for (const v of [
    entity.id,
    entity.POID,
    entity.poid,
    entity.purchaseOrderId,
    entity.PurchaseOrder_Id,
    entity.purchaseOrder_Id,
  ]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return null;
};

const getPoItems = (po) => {
  if (!po || typeof po !== "object") return [];
  const items = po.purchaseOrderItems || po.PurchaseOrderItems;
  return Array.isArray(items) ? items : [];
};

/** Prefer non-empty line items — create response / early GET often omit them. */
const coalescePurchaseOrderItems = (...sources) => {
  let empty = [];
  for (const src of sources) {
    const items = getPoItems(src);
    if (items.length > 0) return items;
    if (items) empty = items;
  }
  return empty;
};

const isInvalidProductId = (id) => {
  if (id == null) return true;
  const n = String(id).trim().toLowerCase();
  return n === "" || n === "null" || n === "undefined";
};

const pickProductId = (...candidates) => {
  for (const v of candidates) {
    if (isInvalidProductId(v)) continue;
    return String(v).trim();
  }
  return "";
};

/** Merge API line items with client fallback names/ids (create GET often strips both). */
const enrichPoLineItems = (items = [], fallbackSources = []) => {
  const fallbacks = coalescePurchaseOrderItems(...fallbackSources);
  return (items || []).map((item, idx) => {
    const fb =
      fallbacks[idx] ||
      fallbacks.find(
        (f) =>
          pickProductId(f?.Product_Id, f?.product_Id, f?.productId) &&
          pickProductId(f?.Product_Id, f?.product_Id, f?.productId) ===
            pickProductId(item?.Product_Id, item?.product_Id, item?.productId)
      );

    const productId = pickProductId(
      item?.Product_Id,
      item?.product_Id,
      item?.productId,
      fb?.Product_Id,
      fb?.product_Id,
      fb?.productId
    );

    const name =
      cleanInventoryProductName(item?.name) ||
      cleanInventoryProductName(item?.productName) ||
      cleanInventoryProductName(item?.remarks) ||
      cleanInventoryProductName(fb?.name) ||
      cleanInventoryProductName(fb?.productName) ||
      cleanInventoryProductName(fb?.remarks) ||
      "";

    return {
      ...item,
      ...(productId
        ? { Product_Id: productId, product_Id: productId, productId }
        : {}),
      ...(name
        ? {
            name,
            remarks: cleanInventoryProductName(item?.remarks) || name,
          }
        : {}),
    };
  });
};

const pickVendorId = (entity) => {
  if (!entity || typeof entity !== "object") return "";
  for (const v of [entity.Vendor_Id, entity.vendor_Id, entity.vendorId]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
  }
  return "";
};

const pickStatus = (entity) => {
  if (!entity || typeof entity !== "object") return "";
  for (const v of [entity.status, entity.Status, entity.lifecycleStatus]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
  }
  return "";
};

const isWeakPoStatus = (status) => {
  const s = String(status || "").trim().toUpperCase();
  return !s || s === "DRAFT" || s === "NULL";
};

const normalizePo = (po, { preserveStatus } = {}) => {
  if (!po) return null;
  const entity = Array.isArray(po) ? po[0] : po;
  const id = extractPurchaseOrderId(entity);
  if (!id) return null;
  const items = coalescePurchaseOrderItems(entity);
  // Strip null poid + nested mappedVendor — name comes from Redux vendors via vendor_Id
  const {
    poid: _ignoredPoid,
    POID: _ignoredPoid2,
    mappedVendor: _ignoredMappedVendor,
    MappedVendor: _ignoredMappedVendor2,
    Vendor_Id: _ignoredVendorId,
    vendor_Id: _ignoredVendorId2,
    vendorId: _ignoredVendorId3,
    status: _ignoredStatus,
    ...rest
  } = entity;
  const vendorId = pickVendorId(entity);
  const parsedStatus = pickStatus(entity);
  const status =
    parsedStatus ||
    (preserveStatus && !isWeakPoStatus(preserveStatus) ? preserveStatus : "") ||
    "Draft";
  return {
    ...rest,
    POID: id,
    id,
    poid: id,
    status,
    // Only set vendor keys when known — never force null (wipes prior upsert / shows GUEST)
    ...(vendorId
      ? { Vendor_Id: vendorId, vendor_Id: vendorId }
      : {}),
    purchaseOrderItems: enrichPoLineItems(items),
    _clientUpsertAt: entity._clientUpsertAt || Date.now(),
  };
};

/**
 * One page of purchase orders (headers only).
 * @param {{ start?: number, size?: number, context?: string, silent?: boolean, hideLoader?: boolean }} [opts]
 */
export const getPurchaseOrders = async (opts = {}) => {
  const start = Number.isFinite(opts.start) ? opts.start : 0;
  const size = Number.isFinite(opts.size) ? opts.size : PURCHASE_ORDERS_PAGE_SIZE;
  const data = await apiClient.get(
    `/PurchaseOrderManagement/purchaseOrdersWithoutDetails?start=${start}&size=${size}`,
    {
      context: opts.context || "Loading Purchase Orders",
      silent: opts.silent,
      hideLoader: opts.hideLoader,
    }
  );
  const chunk = Array.isArray(data) ? data : data ? [data] : [];
  // withoutDetails returns id + null poid/items — normalize so UI always has POID
  return chunk.map((po) => normalizePo(po)).filter(Boolean);
};

/**
 * Fetch all POs by paging. Includes line items (for amount/item count on list).
 * Inventory hydrate is still skipped on this endpoint.
 */
export const getAllPurchaseOrdersPaged = async (opts = {}) => {
  const pageSize = Number.isFinite(opts.size) ? opts.size : PURCHASE_ORDERS_PAGE_SIZE;
  const context = opts.context || "Syncing Purchase Orders";
  const all = [];
  let start = 0;
  // Hard cap pages so a buggy API cannot loop forever (~100k rows)
  const maxPages = 500;

  for (let page = 0; page < maxPages; page += 1) {
    const chunk = await getPurchaseOrders({
      start,
      size: pageSize,
      context,
      silent: page > 0 ? true : opts.silent,
      hideLoader: page > 0,
    });
    all.push(...chunk);
    if (chunk.length < pageSize) break;
    start += pageSize;
  }
  return all;
};

/** Single PO with lines — use after GRN/Accept to sync Redux without reloading the full list.
 *  opts.light=true skips vendor+inventory hydrate (industrial GRN path — much faster).
 */
export const getPurchaseOrderById = async (id, opts = {}) => {
  if (!id) throw new Error("Purchase order id is required");
  const light = opts.light === true;
  const qs = light ? "?withInventory=false&withVendor=false" : "";
  const data = await apiClient.get(`/PurchaseOrderManagement/purchaseOrder/${id}${qs}`, {
    context: opts.context || "Loading Purchase Order",
    silent: opts.silent !== false,
    hideLoader: opts.hideLoader !== false,
  });
  const po = Array.isArray(data) ? data[0] : data;
  if (!po) throw new Error(`Purchase order ${id} not found`);
  return po;
};

export const createPurchaseOrder = async (data, _token) => {
  const result = await apiClient.post("/PurchaseOrderManagement/purchaseOrder", data || {}, {
    context: "Creating Purchase Order",
  });
  // Backend may return a single PO or a one-element list
  return Array.isArray(result) ? result[0] : result;
};

/**
 * Existing POS stock-IN endpoint.
 * Body must be a JSON array of purchase-order line items (not a PO wrapper).
 * Each item needs id/POID of the line + quantity received.
 */
export const applyPurchaseOrderItemsQtyWise = async (data, _token) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.purchaseOrderItems)
      ? data.purchaseOrderItems
      : [];

  const payload = items.map((item) => {
    const lineId = item.id ?? item.POID ?? item.poid ?? item.purchaseOrderItemId;
    return {
      id: lineId != null ? String(lineId) : undefined,
      POID: lineId != null ? String(lineId) : undefined,
      quantity: Number(item.quantity) || 0,
      Product_Id: item.Product_Id || item.product_Id || item.ProductSpecification_Id || item.productId,
      ProductSpecification_Id:
        item.ProductSpecification_Id || item.Product_Id || item.product_Id || item.productId,
      pricePerUnit: item.pricePerUnit,
      name: item.name || item.productName || item.remarks,
      remarks: item.remarks || item.name || item.productName,
      PurchaseOrder_Id: item.PurchaseOrder_Id || item.purchaseOrder_Id,
    };
  });

  return apiClient.put(
    "/PurchaseOrderManagement/applyPurchaseOrderItemsQtyWise",
    payload,
    { context: "Applying purchase order stock" }
  );
};

/**
 * Vendor confirmation / lifecycle status (confirm PO before GRN).
 * PUT /PurchaseOrderManagement/purchaseOrder/{id} with { status: "ACCEPTED"|"REJECTED"|... }
 * Blocked server-side once goods receipt has started.
 */
export const updatePurchaseOrder = async (id, data, _token) => {
  if (!id) {
    throw new Error("Purchase order id is required");
  }
  return apiClient.put(
    `/PurchaseOrderManagement/purchaseOrder/${id}`,
    data || {},
    { context: "Updating Purchase Order status" }
  );
};

export const acceptPurchaseOrder = (id) =>
  updatePurchaseOrder(id, { status: "ACCEPTED" });

export const rejectPurchaseOrder = (id) =>
  updatePurchaseOrder(id, { status: "REJECTED" });

/** Put one PO into Redux (create or replace). Call only after a successful API. */
export const upsertPurchaseOrderInRedux = (dispatch, po) => {
  const normalized = normalizePo(po);
  if (!normalized || !dispatch) return null;
  dispatch({ type: "UPSERT_PURCHASE_ORDER", purchaseorder: normalized });
  return normalized;
};

/**
 * Load full PO (with line items) when list/header payload has null items.
 * withoutDetails is intentional for scale — hydrate only when needed.
 */
export const ensurePurchaseOrderDetails = async (dispatch, poOrId, opts = {}) => {
  const id = extractPurchaseOrderId(poOrId);
  if (!id) return null;

  const existing = typeof poOrId === "object" && poOrId ? poOrId : null;
  if (!opts.force && getPoItems(existing).length > 0) {
    return normalizePo(existing);
  }

  const preserveStatus = pickStatus(existing);

  const fresh = await getPurchaseOrderById(id, {
    light: opts.light === true,
    silent: true,
    hideLoader: true,
  });
  const normalized = normalizePo(fresh, { preserveStatus });
  if (dispatch) {
    return upsertPurchaseOrderInRedux(dispatch, normalized);
  }
  return normalized;
};

/**
 * After create/update API success: upsert PO into Redux from write payload / fallback.
 * No verify GET-by-id — hole-fill thin lines via ensurePurchaseOrderDetails on detail open.
 */
export const syncPurchaseOrderIntoRedux = async (dispatch, poId, fallbackPo = null) => {
  const id = extractPurchaseOrderId(poId) || extractPurchaseOrderId(fallbackPo);
  if (!id && !fallbackPo) return null;

  const fallbackVendor = pickVendorId(fallbackPo);
  const fallbackStatus = pickStatus(fallbackPo);

  return upsertPurchaseOrderInRedux(dispatch, {
    ...(fallbackPo || {}),
    POID: id,
    id,
    poid: id,
    status: fallbackStatus || "Draft",
    ...(fallbackVendor
      ? { Vendor_Id: fallbackVendor, vendor_Id: fallbackVendor }
      : {}),
    purchaseOrderItems: coalescePurchaseOrderItems(fallbackPo),
    _clientUpsertAt: Date.now(),
  });
};
